import { onError } from "@apollo/client/link/error";
import { addBreadcrumb, captureException, type SeverityLevel } from "@sentry/react";

import { unauthorizedErrorRedirect } from "@helpers/unauthorizedErrorRedirect/unauthorizedErrorRedirect";
import type { ErrorNotice } from "@shared/context/ErrorNotifierContext";
import { turnOffErrorReporting } from "@shared/utils/initSentry/filters/turnErrorsOff/turnErrorsOff";

// Matches the gem constant `GraphQL::PersistedQueries::NotFound::MESSAGE` (Ruby-side, not
// importable here). An APQ cache-miss is a normal client-retry handshake — Apollo resends the
// full query text — not a user-facing error, so it must never be forwarded to the toast path.
const PERSISTED_QUERY_NOT_FOUND_MESSAGE = "PersistedQueryNotFound";

let hasNotifiedOffline = false;

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    hasNotifiedOffline = false;
  });
}

interface ErrorLinkParams {
  criticalSnackbar?: (message: string) => void;
  notify?: (notice: ErrorNotice) => void;
  disableUnauthorizedCheck?: boolean;
}

const getErrorLink = ({
  criticalSnackbar,
  notify,
  disableUnauthorizedCheck,
}: ErrorLinkParams = {}) => {
  return onError(({ graphQLErrors, networkError, response, operation }) => {
    addBreadcrumb({
      category: "apolloOnError",
      data: {
        operationName: operation.operationName,
        networkError,
        response: JSON.stringify(response),
        graphQLErrors: JSON.stringify(graphQLErrors),
      },
      level: "info" as SeverityLevel,
    });

    const statusCode = networkError && "statusCode" in networkError && networkError.statusCode;
    const isEmancipatedDependent = graphQLErrors?.[0]?.extensions?.code === "EMANCIPATED_DEPENDENT";
    const suppressOfflineNotification = operation.getContext().suppressOfflineNotification === true;

    if (!!statusCode && statusCode !== 403) {
      captureException(networkError, { level: "warning" });
    }

    if (networkError) {
      turnOffErrorReporting();

      if (
        !suppressOfflineNotification &&
        !statusCode &&
        typeof navigator !== "undefined" &&
        !navigator.onLine &&
        !hasNotifiedOffline
      ) {
        hasNotifiedOffline = true;
        criticalSnackbar?.("Unable to connect. Check your internet connection and try again.");
      }
    }

    if (statusCode === 403 && isEmancipatedDependent) {
      window.location.replace("/accounts");
    }

    const { skipGlobalErrorNotice } = operation.getContext();

    if (graphQLErrors && notify && !skipGlobalErrorNotice) {
      graphQLErrors
        .filter(error => error.message !== PERSISTED_QUERY_NOT_FOUND_MESSAGE)
        .forEach(error => notify({ error }));
    }

    if (!disableUnauthorizedCheck) {
      unauthorizedErrorRedirect(networkError);
    }
  });
};

export { getErrorLink };
